import React, {useState, useContext, useEffect} from "react";
import {FormattedMessage, useIntl} from "react-intl";
import "./TopicDescriptionCreation.css";
import API, {Resolution} from "../../../logic/API";
import searchIcon from "../../../images/search.svg";
import editIcon from "../../../images/simpleEdit.svg";
import userIcon from "../../../images/user.svg";
import chevronIcon from "../../../images/chevron.svg";
import ellipsisIcon from "../../../images/ellipsis.svg";
import {
  AuthMethod,
  OutgoingTopicDescription,
  TopicDescription,
  TopicDescriptionGraphQLTransfer,
  TopicDescriptionInfo,
  TopicDescriptionMqttTransfer,
  TopicDescriptionMqttTransferResponse,
  TopicDescriptionPullTransfer,
  TopicDescriptionPushTransfer,
  TopicDescriptionPushTransferResponse,
} from "../../../logic/APITypes";
import {
  Button,
  IconButton,
  DropDown,
  TextArea,
  TextInput,
  RadioButtonGroup,
  ModalContext,
  DropDownOption,
  Icon,
  Checkbox,
} from "@spg/component_library";
import {SnackbarContext} from "../../../reusables/SnackbarProvider";
import {ScreenSize} from "../../utility/ScreenSize";
import {PushTransferSection} from "./PushTransferSection";
import {PullTransferSection} from "./PullTransferSection";
import {StorageSection} from "./StorageSection";
import {UploadSection, UploadTransferSection} from "./UploadSection";
import {FolderSection, FolderTransferSection} from "./FolderSection";
import {NavigationManagement} from "../../LandingPage";
import {DefaultInputBehavior} from "../../../reusables/DefaultInputBehavior";
import {TransferOwnershipList} from "./TransferOwnershipList";
import {useNavigate} from "react-router-dom";
import {MqttTransferSection} from "./MqttTransferSection";
import {GraphQLTransferSection} from "./GraphQLTransferSection";
import {AppSettingsContext} from "../../utility/AppSettingsProvider";

/**
 * A page with a form allowing for the creation and editing of topic descriptions.
 */
function TopicDescriptionCreation(props: {
  alternative: "create" | "view" | "edit";
  navigationManagement: NavigationManagement;
  viewEditInfo?: {
    onSuccessfulEdit: (newTopic: TopicDescription) => void;
    onBack: () => void;
    onTransferred: (newOwnerName: string) => void;
    initialTopic: Resolution<TopicDescription>;
    initialInfo: TopicDescriptionInfo;
  };
  onSuccessfulCreation?: (topic: TopicDescription) => void;
  onCancel?: () => void;
}) {
  const addSnackbar = useContext(SnackbarContext).addSnackbar;
  const modalContext = useContext(ModalContext);
  const {wikiUrl} = useContext(AppSettingsContext);
  const screenSizeWidth = useContext(ScreenSize).width;
  const intl = useIntl();
  const navigate = useNavigate();

  const [alternative, setAlternative] = useState(props.alternative);
  const [requestedToLeaveView, setRequestedToLeaveView] = useState<
    "back" | "edit"
  >();
  const [menuOpen, setMenuOpen] = useState(false);

  // States for tracking form contents and potential errors
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState<
    OutgoingTopicDescription["status"] | null
  >(null);
  const [visibility, setVisibility] = useState<
    TopicDescription["visibility"] | null
  >(null);
  const methodOptionValues = [
    "rest_pull",
    "rest_push",
    "file_upload",
    "mqtt",
    "graphql",
    "multi_file_upload",
  ];
  const methodOptions: DropDownOption<(typeof methodOptionValues)[number]>[] =
    methodOptionValues.map((value) => ({
      value,
      label: intl.formatMessage({
        id: "topicDescriptionConnector.method." + value,
      }),
    }));
  const [method, setMethod] = useState<null | DropDownOption<
    (typeof methodOptionValues)[number]
  >>(null);
  const [authentication, setAuthentication] = useState<AuthMethod>("NULL");
  const [dataProtectionAccepted, setDataProtectionAccepted] = useState(false);
  const [waitingForResponse, setWaitingForResponse] = useState(false);
  const [pushCreationResponse, setPushCreationResponse] =
    useState<TopicDescriptionPushTransferResponse>();
  const [mqttCreationResponse, setMqttCreationResponse] =
    useState<TopicDescriptionMqttTransferResponse>();
  const [pullTransferNullTrigger, setPullTransferNullTrigger] = useState(0);
  const [pullTransferInitTrigger, setPullTransferInitTrigger] = useState(0);
  const [storageNullTrigger, setStorageNullTrigger] = useState(0);
  const [storageInitTrigger, setStorageInitTrigger] = useState(0);
  const [pullInfo, setPullInfo] = useState<{
    unmodifiedInputs: boolean;
    validInputs: boolean;
    strategy: TopicDescription["information_strategy"];
    transfer: TopicDescriptionPullTransfer | TopicDescriptionGraphQLTransfer;
  }>();
  const [storageInfo, setStorageInfo] = useState<{
    unmodifiedInputs: boolean;
    validInputs: boolean;
    maxAgeInS: TopicDescription["data_max_age"];
  }>();
  const [createdTopic, setCreatedTopic] = useState<TopicDescription>();

  const initialTopic = props.viewEditInfo?.initialTopic.payload;

  const visibilityMessage =
    props.viewEditInfo?.initialInfo.visibility === "PUBLIC" &&
    visibility === "PRIVATE"
      ? "switchedToPrivate"
      : visibility === "PUBLIC" &&
          (alternative === "create" ||
            props.viewEditInfo?.initialInfo.visibility === "PRIVATE")
        ? "setAsPublic"
        : "";

  const homePageIsOrigin =
    localStorage.getItem("topicDescriptionCreationOriginPage") === "home";

  /** Initializes form with values based on the initialTopic if available,
   * otherwise only info available from the overview table is filled in.
   * For the creation alternatives default values (mostly empty) are used. */
  const setInitialStates = () => {
    setName(initialTopic?.name || props.viewEditInfo?.initialInfo.name || "");
    setDescription(
      initialTopic?.description ||
        props.viewEditInfo?.initialInfo.description ||
        "",
    );
    setStatus(
      initialTopic?.health.startsWith("ACTIVE") ||
        props.viewEditInfo?.initialInfo.health.startsWith("ACTIVE")
        ? "START"
        : "STOP",
    );
    setVisibility(
      initialTopic?.visibility ||
        props.viewEditInfo?.initialInfo.visibility ||
        "PRIVATE",
    );
    setStorageInitTrigger((n) => n + 1);
    setMethod(
      methodOptions.find((dfo) =>
        !initialTopic
          ? false
          : dfo.value ===
            (initialTopic?.transfer.transfer_type === "GRAPHQL"
              ? "graphql"
              : initialTopic?.transfer.direction === "PULL"
                ? "rest_pull"
                : initialTopic?.transfer.transfer_type === "REST"
                  ? "rest_push"
                  : initialTopic?.transfer.transfer_type === "FILE_UPLOAD"
                    ? "file_upload"
                    : initialTopic?.transfer.transfer_type ===
                        "MULTI_FILE_UPLOAD"
                      ? "multi_file_upload"
                      : "mqtt"),
      ) || null,
    );
    setDataProtectionAccepted(false);
    setPullTransferInitTrigger((n) => n + 1);
    setPushCreationResponse(undefined);
    setMqttCreationResponse(undefined);
    setAuthentication(
      (initialTopic?.transfer.transfer_type === "REST" ||
        initialTopic?.transfer.transfer_type === "GRAPHQL") &&
        initialTopic?.transfer.authentication
        ? initialTopic.transfer.authentication.method
        : "NULL",
    );
  };

  /** Empties all fields of the form, so another topic can be created. */
  const resetStates = () => {
    setName("");
    setDescription("");
    setStatus("STOP");
    setVisibility("PRIVATE");
    setStorageNullTrigger((n) => n + 1);
    setPullTransferNullTrigger((n) => n + 1);
    setPushCreationResponse(undefined);
    setMqttCreationResponse(undefined);
    setDataProtectionAccepted(false);
    setMethod(null);
    setCreatedTopic(undefined);
  };

  /** Constructs the object to send to the backend from the form contents.
   * The password is only included if it was modified. */
  const buildTopicDescription: () => OutgoingTopicDescription | false = () => {
    let transfer: TopicDescription["transfer"];
    if (method?.value === "rest_pull" || method?.value === "graphql") {
      if (!pullInfo) return false;
      transfer = pullInfo?.transfer;
    } else if (method?.value === "rest_push") {
      if (!authentication || authentication === "NULL") return false;
      transfer = {
        transfer_type: "REST",
        direction: "PUSH",
        authentication: {method: authentication},
      };
    } else if (method?.value === "file_upload") {
      transfer = {
        transfer_type: "FILE_UPLOAD",
        direction: "PUSH",
      };
    } else if (method?.value === "multi_file_upload") {
      transfer = {
        transfer_type: "MULTI_FILE_UPLOAD",
        direction: "PUSH",
      };
    } else if (method?.value === "mqtt") {
      transfer = {
        transfer_type: "MQTT",
        direction: "PUSH",
      };
    } else return false;
    if (!visibility) return false;
    if (!status) return false;

    return {
      name: name,
      description: description,
      status: status,
      visibility: visibility,
      transfer,
      information_strategy: pullInfo?.strategy || "EMAIL",
      data_max_age: storageInfo?.maxAgeInS || null,
      is_data_protection_accepted: dataProtectionAccepted,
    };
  };

  /** Handles editing of the TopicDescription. */
  const onUpdate = () => {
    modalContext.showModal({
      idBase: "topicDescriptionCreationSave",
      headline: intl.formatMessage({
        id: "topicDescriptionCreation.modal.save.headline",
      }),
      body: intl.formatMessage({
        id: "topicDescriptionCreation.modal.save.text",
      }),
      primaryButton: {
        name: intl.formatMessage({
          id: "topicDescriptionCreation.modal.save.save",
        }),
        onClick: () => {
          const topic = buildTopicDescription();
          if (!topic || !props.viewEditInfo) return;
          setWaitingForResponse(true);
          API.updateTopicDescription(
            props.viewEditInfo.initialInfo.id,
            topic,
          ).then((response) => {
            if (response.success) {
              if (props.viewEditInfo) {
                const newTopic: TopicDescription = {
                  ...topic,
                  id: props.viewEditInfo.initialInfo.id,
                  health:
                    topic.status === "START" ? "ACTIVE_NO_ERROR" : "INACTIVE",
                };
                props.viewEditInfo.onSuccessfulEdit(
                  topic.transfer.direction === "PUSH" &&
                    topic.transfer.transfer_type === "REST"
                    ? {
                        ...newTopic,
                        transfer: {
                          ...topic.transfer,
                          url: (
                            initialTopic?.transfer as TopicDescriptionPushTransfer
                          ).url,
                        } as TopicDescriptionPushTransfer,
                      }
                    : topic.transfer.transfer_type === "MQTT"
                      ? {
                          ...newTopic,
                          transfer: {
                            ...(initialTopic?.transfer as TopicDescriptionMqttTransfer),
                            ...topic.transfer,
                          } as TopicDescriptionMqttTransfer,
                        }
                      : newTopic,
                );
              }
              setAlternative("view");
            } else {
              addSnackbar({
                variant: "close",
                text: intl.formatMessage({
                  id: "topicDescriptionCreation.snackbar.edit.error.text",
                }),
                hasCloseButton: true,
              });
            }
            setWaitingForResponse(false);
          });
        },
      },
      secondaryButton: {
        name: intl.formatMessage({
          id: "topicDescriptionCreation.modal.save.cancel",
        }),
        onClick: () => undefined,
      },
    });
  };

  /** Handles creation of the TopicDescription. */
  const onCreate = () => {
    const topic = buildTopicDescription();
    if (!topic) return;
    setWaitingForResponse(true);
    API.createTopicDescription(
      topic,
      (topic.transfer.direction === "PUSH" &&
        topic.transfer.transfer_type === "REST") ||
        topic.transfer.transfer_type === "MQTT",
    ).then((response) => {
      setWaitingForResponse(false);
      const newTopic: TopicDescription = {
        ...topic,
        id: response.payload?.id || "",
        health: topic.status === "START" ? "ACTIVE_NO_ERROR" : "INACTIVE",
      };
      setCreatedTopic(newTopic);
      if (
        topic.transfer.direction === "PULL" ||
        topic.transfer.transfer_type === "FILE_UPLOAD" ||
        topic.transfer.transfer_type === "MULTI_FILE_UPLOAD"
      ) {
        if (response.success) {
          if (props.onSuccessfulCreation) props.onSuccessfulCreation(newTopic);
          resetStates();
          addSnackbar({
            variant: "timeout",
            text: intl.formatMessage({
              id: "topicDescriptionCreation.snackbar.create.success.text",
            }),
            timeout: 7000,
          });
        } else {
          addSnackbar({
            variant: "close",
            text: intl.formatMessage({
              id: "topicDescriptionCreation.snackbar.create.error.text",
            }),
            hasCloseButton: true,
          });
        }
      } else {
        if (response.success && response.payload) {
          if ("connection_string" in response.payload)
            setMqttCreationResponse(response.payload);
          else if ("url" in response.payload)
            setPushCreationResponse(response.payload);
        } else {
          addSnackbar({
            variant: "close",
            text: intl.formatMessage({
              id: "topicDescriptionCreation.snackbar.create.error.text",
            }),
            hasCloseButton: true,
          });
        }
      }
    });
  };

  const unmodifiedInputs =
    name === initialTopic?.name &&
    description === initialTopic?.description &&
    ((status === "START" && initialTopic?.health.startsWith("ACTIVE")) ||
      (status === "STOP" && !initialTopic?.health.startsWith("ACTIVE"))) &&
    visibility === initialTopic.visibility &&
    storageInfo?.unmodifiedInputs &&
    (initialTopic.transfer.direction === "PUSH" || pullInfo?.unmodifiedInputs);

  const emptyInputs =
    name === "" &&
    description === "" &&
    status === "STOP" &&
    visibility === "PRIVATE" &&
    storageInfo?.maxAgeInS === null &&
    method === null &&
    !dataProtectionAccepted;

  const validInputsPush =
    [name, description].every((input) => input.length > 0) &&
    status &&
    visibility &&
    storageInfo?.validInputs &&
    method &&
    dataProtectionAccepted;

  const validInputsPull = validInputsPush && pullInfo?.validInputs;

  useEffect(() => {
    if (alternative != "create") return;
    if (method?.value === "rest_push" && authentication === "NULL")
      setAuthentication("BASIC");
    const container = document.querySelector(
      ".transfer.topicDescriptionCreationGroupContainer",
    ) as HTMLElement;
    const content = document.querySelector(
      ".transfer > .topicDescriptionCreationGroupContent",
    ) as HTMLElement;
    if (!container || !content) return;
    const shouldScroll = container.clientHeight + 32 < content.scrollHeight;
    container.style.height =
      (method?.value ? content.scrollHeight : 0) + 32 + "px";
    const main = document.getElementById("main");
    if (!shouldScroll) return;
    const interval = window.setInterval(() => {
      main?.scrollBy(0, 10);
    }, 0);
    container.ontransitionend = () => {
      clearInterval(interval);
    };
    return () => {
      clearInterval(interval);
    };
  }, [method, screenSizeWidth]);

  useEffect(setInitialStates, [props.viewEditInfo?.initialTopic]);

  useEffect(() => {
    if (props.navigationManagement.isTryingToNavigate.length === 0) return;
    if (alternative !== "view" || method?.value !== "file_upload")
      props.navigationManagement.allowNavigation();
  }, [props.navigationManagement.isTryingToNavigate]);

  useEffect(() => {
    const container = document.querySelector(
      ".topicDescriptionCreationGroupContainer.visibility",
    ) as HTMLElement;
    const content = document.querySelector(
      ".visibility > .topicDescriptionCreationGroupContent",
    ) as HTMLElement;
    if (!container || !content) return;
    container.style.height = content.scrollHeight + "px";
  }, [visibilityMessage, screenSizeWidth]);

  return (
    <div className={"topicDescriptionCreationContainer " + alternative}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
        }}
      >
        <h1>
          {alternative === "create" ? (
            <span>
              <FormattedMessage
                id="topicDescriptionCreation.formTitle.create"
                values={{b: (chunks) => <b>{chunks}</b>}}
              />
            </span>
          ) : (
            <>
              {alternative !== "edit" && (
                <IconButton
                  idBase={"topicDescriptionCreationBack"}
                  onClick={() => {
                    if (
                      alternative !== "view" ||
                      method?.value !== "file_upload"
                    ) {
                      if (homePageIsOrigin) navigate("/home");
                      else props.viewEditInfo?.onBack();
                    } else setRequestedToLeaveView("back");
                  }}
                  iconProps={{
                    href: chevronIcon + "#chevronLeft",
                    title: intl.formatMessage({
                      id: "topicDescriptionCreation.formTitle.back.button",
                    }),
                    strokeColor: "black",
                  }}
                  hoverColor="#ff3d00"
                  buttonSize={{height: 36, width: 36}}
                />
              )}
              <span className="topicDescriptionCreationTitle">
                <FormattedMessage
                  id="topicDescriptionCreation.formTitle.edit"
                  values={{
                    b: (chunks) => <b>{chunks}</b>,
                    topicName: props.viewEditInfo?.initialInfo.name || "",
                  }}
                />
              </span>
              {alternative === "view" && (
                <span className="topicDescriptionCreationMenu">
                  <button
                    id="topicDescriptionCreationMenuButton"
                    className="topicDescriptionCreationMenuButton"
                    onClick={() => {
                      setMenuOpen((x) => !x);
                    }}
                    aria-haspopup="menu"
                    aria-controls="topicDescriptionCreationMenuContainer"
                  >
                    <Icon
                      href={ellipsisIcon + "#globalG"}
                      title={intl.formatMessage({
                        id: "topicDescriptionCreation.formTitle.openMenu.button",
                      })}
                    />
                  </button>
                  {menuOpen && (
                    <>
                      <menu id="topicDescriptionCreationMenuContainer">
                        <li>
                          <button
                            id="topicDescriptionCreationContentViewerButton"
                            className="topicDescriptionCreationMenuButton fill"
                            onClick={() => {
                              setMenuOpen(false);
                              if (initialTopic)
                                navigate("/topics/datasets/" + initialTopic.id);
                            }}
                          >
                            <Icon href={searchIcon + "#globalG"} title="" />
                            <FormattedMessage id="topicDescriptionCreation.formTitle.menu.contentViewer" />
                          </button>
                        </li>
                        {props.viewEditInfo?.initialInfo.edit_authorized && (
                          <li>
                            <button
                              id="topicDescriptionCreationEditIconButton"
                              className="topicDescriptionCreationMenuButton"
                              onClick={() => {
                                setMenuOpen(false);
                                if (
                                  alternative !== "view" ||
                                  method?.value !== "file_upload"
                                )
                                  setAlternative("edit");
                                else setRequestedToLeaveView("edit");
                              }}
                            >
                              <Icon href={editIcon + "#globalG"} title="" />
                              <FormattedMessage id="topicDescriptionCreation.formTitle.menu.edit" />
                            </button>
                          </li>
                        )}
                        {props.viewEditInfo?.initialInfo.is_owner && (
                          <li>
                            <button
                              id="topicDescriptionCreationTransferOwnershipButton"
                              className="topicDescriptionCreationMenuButton"
                              onClick={() => {
                                setMenuOpen(false);
                                modalContext.showModal({
                                  idBase:
                                    "topicDescriptionCreationTransferOwnership",
                                  headline: intl.formatMessage({
                                    id: "topicDescriptionCreation.transferOwnership.modal.headline",
                                  }),
                                  body: (
                                    <div className="modalText">
                                      <FormattedMessage
                                        id="topicDescriptionCreation.transferOwnership.modal.body"
                                        values={{
                                          b: (chunks) => <b>{chunks}</b>,
                                          br: () => <br />,
                                        }}
                                      />
                                    </div>
                                  ),
                                  // Buttons aren't displayed, instead they are managed as part of the additionalContent
                                  primaryButton: {
                                    name: "",
                                    onClick: () => undefined,
                                  },
                                  secondaryButton: {
                                    name: "",
                                    onClick: () => undefined,
                                  },
                                  additionalContent: (
                                    <TransferOwnershipList
                                      topicId={
                                        props.viewEditInfo?.initialInfo.id || ""
                                      }
                                      onTransferred={(newOwnerName) => {
                                        modalContext.hideModal();
                                        setAlternative("view");
                                        props.viewEditInfo?.onTransferred(
                                          newOwnerName,
                                        );
                                      }}
                                      onCancel={() => {
                                        modalContext.hideModal();
                                      }}
                                    />
                                  ),
                                });
                              }}
                            >
                              <Icon href={userIcon + "#globalG"} title="" />
                              <FormattedMessage id="topicDescriptionCreation.formTitle.menu.transfer" />
                            </button>
                          </li>
                        )}
                      </menu>
                    </>
                  )}
                </span>
              )}
            </>
          )}
        </h1>
        <div
          className={"topicDescriptionCreationEditingContainer " + alternative}
        >
          {alternative === "create" && (
            <p className="topicDescriptionCreationExplanation">
              <FormattedMessage
                id="topicDescriptionCreation.explanation"
                values={{
                  a: (chunks) => (
                    <a href={wikiUrl} target="_blank" rel="noreferrer">
                      {chunks}
                    </a>
                  ),
                }}
              />
            </p>
          )}
          {method?.value === "file_upload" &&
            alternative === "view" &&
            props.viewEditInfo && (
              <UploadSection
                alternative={alternative}
                navigationManagement={
                  requestedToLeaveView
                    ? {
                        isTryingToNavigate: "requestedToLeaveView",
                        allowNavigation: () => {
                          setRequestedToLeaveView(undefined);
                          if (requestedToLeaveView === "edit")
                            setAlternative("edit");
                          else if (homePageIsOrigin) navigate("/home");
                          else props.viewEditInfo?.onBack();
                        },
                        preventNavigation: () => {
                          setRequestedToLeaveView(undefined);
                        },
                      }
                    : props.navigationManagement
                }
                topicId={props.viewEditInfo?.initialInfo.id}
                disabledReason={
                  status === "STOP"
                    ? "inactive"
                    : props.viewEditInfo.initialInfo.is_owner
                      ? undefined
                      : "unauthorized"
                }
              />
            )}
          {method?.value === "multi_file_upload" &&
            alternative === "view" &&
            props.viewEditInfo && (
              <FolderSection
                alternative={alternative}
                navigationManagement={
                  requestedToLeaveView
                    ? {
                        isTryingToNavigate: "requestedToLeaveView",
                        allowNavigation: () => {
                          setRequestedToLeaveView(undefined);
                          if (requestedToLeaveView === "edit")
                            setAlternative("edit");
                          else if (homePageIsOrigin) navigate("/home");
                          else props.viewEditInfo?.onBack();
                        },
                        preventNavigation: () => {
                          setRequestedToLeaveView(undefined);
                        },
                      }
                    : props.navigationManagement
                }
                topicId={props.viewEditInfo?.initialInfo.id}
                disabledReason={
                  status === "STOP"
                    ? "inactive"
                    : props.viewEditInfo.initialInfo.is_owner
                      ? undefined
                      : "unauthorized"
                }
              />
            )}
          <h2>
            {<FormattedMessage id="topicDescriptionCreation.h2.general" />}
          </h2>
          <div className="topicDescriptionCreationNameContainer">
            <DefaultInputBehavior
              component={TextInput}
              label={intl.formatMessage({
                id: "topicDescriptionCreation.inputLabel.name",
              })}
              idBase={"topicDescriptionCreationName"}
              value={name}
              setValue={setName}
              isRequired
              isDisabled={waitingForResponse}
              isReadOnly={
                alternative === "view" ||
                props.viewEditInfo?.initialTopic.unresolved
              }
              isEdited={initialTopic && initialTopic?.name !== name}
            />
          </div>
          <div className="topicDescriptionCreationDescriptionContainer">
            <DefaultInputBehavior
              component={TextArea}
              label={intl.formatMessage({
                id: "topicDescriptionCreation.inputLabel.description",
              })}
              idBase={"topicDescriptionCreationDescription"}
              value={description}
              setValue={setDescription}
              maxLength={850}
              rows={5}
              supportingText={
                alternative !== "view" && description.trim().length > 0
                  ? intl.formatMessage(
                      {
                        id: "topicDescriptionCreation.inputLabel.description.remaining",
                      },
                      {charCount: 850 - description.length},
                    )
                  : alternative !== "view"
                    ? ""
                    : undefined
              }
              isDisabled={waitingForResponse}
              isReadOnly={
                alternative === "view" ||
                props.viewEditInfo?.initialTopic.unresolved
              }
              isEdited={
                initialTopic && initialTopic?.description !== description
              }
              isRequired
            />
          </div>
          <div className="topicDescriptionCreationStatusContainer">
            <RadioButtonGroup
              label={intl.formatMessage({
                id: "topicDescriptionCreation.inputLabel.status",
              })}
              idBase={"topicDescriptionCreationStatus"}
              name={"status"}
              options={{
                START: intl.formatMessage({
                  id: "topicDescriptionCreation.inputLabel.status.active",
                }),
                STOP: intl.formatMessage({
                  id: "topicDescriptionCreation.inputLabel.status.inactive",
                }),
              }}
              value={status}
              setValue={(v) => {
                setStatus(v as "START" | "STOP" | null);
              }}
              isDisabled={waitingForResponse}
              isReadOnly={
                alternative === "view" ||
                props.viewEditInfo?.initialTopic.unresolved
              }
            />
          </div>
          <div className="topicDescriptionCreationVisibilityContainer">
            <RadioButtonGroup
              label={intl.formatMessage({
                id: "topicDescriptionCreation.inputLabel.visibility",
              })}
              idBase={"topicDescriptionCreationVisibility"}
              name={"visibility"}
              options={{
                PRIVATE: intl.formatMessage({
                  id: "topicDescriptionCreation.inputLabel.visibility.private",
                }),
                PUBLIC: intl.formatMessage({
                  id: "topicDescriptionCreation.inputLabel.visibility.public",
                }),
              }}
              value={visibility}
              setValue={(v) => {
                setVisibility(v as "PRIVATE" | "PUBLIC" | null);
              }}
              isDisabled={waitingForResponse}
              isReadOnly={
                alternative === "view" ||
                props.viewEditInfo?.initialTopic.unresolved
              }
            />
            {alternative !== "view" && (
              <div
                className={`topicDescriptionCreationGroupContainer active visibility${
                  visibilityMessage === "switchedToPrivate" ? " error" : ""
                }`}
              >
                <div className="topicDescriptionCreationGroupContent">
                  {visibilityMessage && (
                    <FormattedMessage
                      id={`topicDescriptionCreation.p.visibilityMessage.${visibilityMessage}`}
                      values={{b: (chunks) => <b>{chunks}</b>}}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
          <StorageSection
            alternative={alternative}
            waitingForResponse={waitingForResponse}
            storageInitTrigger={storageInitTrigger}
            storageNullTrigger={storageNullTrigger}
            setStorageInfo={(
              unmodifiedInputs: boolean,
              validInputs: boolean,
              maxAgeInS: number | null,
            ) => {
              setStorageInfo({unmodifiedInputs, validInputs, maxAgeInS});
            }}
            initialTopic={props.viewEditInfo?.initialTopic}
          />
          <h2 className="topicDescriptionCreationTransferHeading">
            {<FormattedMessage id="topicDescriptionCreation.h2.transfer" />}
          </h2>
          <div className="topicDescriptionCreationMethodContainer">
            <DefaultInputBehavior
              component={DropDown}
              label={intl.formatMessage({
                id: "topicDescriptionCreation.inputLabel.method",
              })}
              idBase={"topicDescriptionCreationMethod"}
              options={methodOptions}
              selectedOption={method}
              setSelectedOption={(newMethod) =>
                setMethod(newMethod as typeof method)
              }
              isRequired
              isDisabled={waitingForResponse || alternative === "edit"}
              isReadOnly={
                alternative === "view" ||
                props.viewEditInfo?.initialTopic.unresolved
              }
            />
          </div>
          <div
            className={
              "topicDescriptionCreationGroupContainer transfer" +
              (alternative == "create" ? " active" : "")
            }
          >
            <div className="topicDescriptionCreationGroupContent landingPageGrid">
              {method?.value === "rest_pull" && (
                <PullTransferSection
                  alternative={alternative}
                  authentication={authentication}
                  setAuthentication={setAuthentication}
                  waitingForResponse={waitingForResponse}
                  pullTransferNullTrigger={pullTransferNullTrigger}
                  pullTransferInitTrigger={pullTransferInitTrigger}
                  setPullInfo={(
                    unmodifiedInputs: boolean,
                    validInputs: boolean,
                    strategy: TopicDescription["information_strategy"],
                    transfer: TopicDescriptionPullTransfer,
                  ) => {
                    setPullInfo({
                      unmodifiedInputs,
                      validInputs,
                      strategy,
                      transfer,
                    });
                  }}
                  initialTopic={props.viewEditInfo?.initialTopic}
                />
              )}
              {method?.value === "graphql" && (
                <GraphQLTransferSection
                  alternative={alternative}
                  waitingForResponse={waitingForResponse}
                  pullTransferNullTrigger={pullTransferNullTrigger}
                  pullTransferInitTrigger={pullTransferInitTrigger}
                  setPullInfo={(
                    unmodifiedInputs: boolean,
                    validInputs: boolean,
                    strategy: TopicDescription["information_strategy"],
                    transfer: TopicDescriptionGraphQLTransfer,
                  ) => {
                    setPullInfo({
                      unmodifiedInputs,
                      validInputs,
                      strategy,
                      transfer,
                    });
                  }}
                  initialTopic={props.viewEditInfo?.initialTopic}
                  authentication={authentication}
                  setAuthentication={setAuthentication}
                />
              )}
              {method?.value === "rest_push" && (
                <PushTransferSection
                  alternative={alternative}
                  authentication={authentication}
                  setAuthentication={setAuthentication}
                  onCloseModal={() => {
                    if (props.onSuccessfulCreation && createdTopic)
                      props.onSuccessfulCreation(createdTopic);
                    resetStates();
                  }}
                  initialTopic={props.viewEditInfo?.initialTopic}
                  creationResponse={pushCreationResponse}
                />
              )}
              {method?.value === "file_upload" && (
                <UploadTransferSection alternative={alternative} />
              )}
              {method?.value === "multi_file_upload" && (
                <FolderTransferSection alternative={alternative} />
              )}
              {method?.value === "mqtt" && (
                <MqttTransferSection
                  alternative={alternative}
                  initialTopic={props.viewEditInfo?.initialTopic}
                  onCloseModal={() => {
                    if (props.onSuccessfulCreation && createdTopic)
                      props.onSuccessfulCreation(createdTopic);
                    resetStates();
                  }}
                  creationResponse={mqttCreationResponse}
                />
              )}
            </div>
          </div>
        </div>
        <div className="topicDescriptionCreationEditingButtons">
          {alternative === "create" ? (
            <>
              <Button
                label={intl.formatMessage({
                  id: "topicDescriptionCreation.button.cancel",
                })}
                idBase={"topicDescriptionCreationCancelChanges"}
                onClick={() => {
                  if (emptyInputs) {
                    if (props.onCancel) props.onCancel();
                  } else
                    modalContext.showModal({
                      idBase: "topicDescriptionCreationCancel",
                      headline: intl.formatMessage({
                        id: "topicDescriptionCreation.modal.cancel.headline",
                      }),
                      body: intl.formatMessage({
                        id: "topicDescriptionCreation.modal.cancel.text",
                      }),
                      primaryButton: {
                        name: intl.formatMessage({
                          id: "topicDescriptionCreation.modal.cancel.cancel",
                        }),
                        onClick: () => {
                          if (props.onCancel) props.onCancel();
                        },
                      },
                      secondaryButton: {
                        name: intl.formatMessage({
                          id: "topicDescriptionCreation.modal.cancel.back",
                        }),
                        onClick: () => undefined,
                      },
                    });
                }}
                isDisabled={waitingForResponse}
                configuration={"tertiary"}
              />
              <Button
                label={intl.formatMessage({
                  id: "topicDescriptionCreation.button.save",
                })}
                idBase={"topicDescriptionCreationSave"}
                isDisabled={
                  waitingForResponse ||
                  (method?.value === "rest_push" ||
                  method?.value === "file_upload" ||
                  method?.value === "multi_file_upload" ||
                  method?.value === "mqtt"
                    ? !validInputsPush
                    : !validInputsPull)
                }
                isWide
                onClick={onCreate}
                configuration={"primary"}
                isSubmit
              />
            </>
          ) : (
            alternative === "edit" && (
              <>
                <Button
                  label={intl.formatMessage({
                    id: "topicDescriptionCreation.button.cancel",
                  })}
                  idBase={"topicDescriptionCreationCancelChanges"}
                  onClick={() => {
                    if (unmodifiedInputs) {
                      setInitialStates();
                      setAlternative("view");
                    } else
                      modalContext.showModal({
                        idBase: "topicDescriptionCreationCancel",
                        headline: intl.formatMessage({
                          id: "topicDescriptionCreation.modal.cancel.headline",
                        }),
                        body: intl.formatMessage({
                          id: "topicDescriptionCreation.modal.cancel.text",
                        }),
                        primaryButton: {
                          name: intl.formatMessage({
                            id: "topicDescriptionCreation.modal.cancel.cancel",
                          }),
                          onClick: () => {
                            setInitialStates();
                            setAlternative("view");
                          },
                        },
                        secondaryButton: {
                          name: intl.formatMessage({
                            id: "topicDescriptionCreation.modal.cancel.back",
                          }),
                          onClick: () => undefined,
                        },
                      });
                  }}
                  isDisabled={
                    waitingForResponse ||
                    props.viewEditInfo?.initialTopic.unresolved
                  }
                  configuration={"tertiary"}
                />
                <Button
                  label={intl.formatMessage({
                    id: "topicDescriptionCreation.button.save",
                  })}
                  idBase={"topicDescriptionCreationSaveChanges"}
                  onClick={onUpdate}
                  isDisabled={
                    waitingForResponse ||
                    unmodifiedInputs ||
                    (method?.value === "rest_push" ||
                    method?.value === "file_upload" ||
                    method?.value === "multi_file_upload" ||
                    method?.value === "mqtt"
                      ? !validInputsPush
                      : !validInputsPull) ||
                    props.viewEditInfo?.initialTopic.unresolved
                  }
                  configuration={"primary"}
                  isSubmit
                />
              </>
            )
          )}
        </div>
        <div className="topicDescriptionCreationDataProtection topicDescriptionCreationEditingContainer">
          {alternative != "view" && (
            <Checkbox
              label={intl.formatMessage({
                id: "topicDescriptionCreation.checkbox.dataProtection",
              })}
              idBase={"topicDescriptionCreationDataProtection"}
              setValue={setDataProtectionAccepted}
              isChecked={dataProtectionAccepted}
              isDisabled={waitingForResponse}
            />
          )}
        </div>
      </form>
    </div>
  );
}

export {TopicDescriptionCreation};
